<template>
  <div style="padding-top: 12px;">
    <div style="text-align: center;margin-bottom: 7px;">
      <el-select class="select"
                 size="large"
                 clearable
                 placeholder="Job Department"
                 style="padding: 0"
                 @change="setJobDepartmentId"
                 v-model="selectedDepartmentId">
        <el-option v-for="option in allDepartments"
                   class="select"
                   :value="option.value"
                   :label="option.label"
                   :key="option.value">
        </el-option>
      </el-select>
    </div>
    <h5 style="text-align: center; margin-bottom: 8px;">{{ projectName }}</h5>
    <div class="row" v-if="projectNotes.length > 0">
      <div class="col-md-2"></div>
      <div class="col-md-8" style="margin-bottom: 5px; text-align: center; font-size: larger;">
        <span v-html="projectNotes"></span>
      </div>
      <div class="col-md-2"></div>
    </div>
    <div class="row">
      <div class="col-md-12">
        <el-table class="table-striped wrap-whole-words"
                  :data="jobTableData"
                  border
                  style="width: 100%">
          <el-table-column type="expand" width="40" name="tasks">
            <template v-slot="props">
              <div class="row" style="padding: 12px;">
                <div class="col-md-4" v-for="task in props.row.tasks">
                  <card>
                    <div>
                      <label><b>Task:</b> {{ task.name }} </label>
                      <ul>
                        <li v-if="task.ownerId"><strong>Owner:</strong> {{ getUserDisplayName(task.ownerId) }} </li>
                        <li v-if="task.assigneeId"><strong>Assignee:</strong> {{ getUserDisplayName(task.assigneeId) }} </li>
                        <li v-if="task.priority"><strong>Priority:</strong> {{ priorityValueToLabel(task.priority) }} </li>
                        <li v-if="task.status"><strong>Status:</strong> {{ task.status }} </li>
                        <li v-if="task.dueDate"><strong>Due Date:</strong> {{ task.dueDate | formatDate }} </li>
                        <li v-if="task.completionDate"><strong>Completion Date:</strong> {{ task.completionDate | formatDate }} </li>
                        <li v-if="task.notes"><strong>Notes:</strong> <div v-html="task.notes">-</div></li>
                      </ul>
                    </div>
                    <div class="inline-block" v-if="Object.keys(task.fieldValues).length">
                      <label><b>Extra Fields</b></label>
                      <ul>
                        <li v-for="fieldKey in Object.keys(task.fieldValues)"><strong>{{ formatKey(task, fieldKey) }}:</strong> {{ task.fieldValues[fieldKey] }}</li>
                      </ul>
                    </div>
                  </card>
                </div>
              </div>
            </template>
          </el-table-column>
          <el-table-column
            :min-width="200"
            prop="name"
            label="Existing Job Name">
            <template v-slot="props">
              <a href="#" @click.prevent="handleEditJob(props.row.id)"> {{ props.row.name }} </a>
              <el-tooltip v-if="props.row.description" class="item" effect="dark" :content="viewDescriptionNoHtml(props.row.description)" placement="right-end">
                <img class="icon-help m-left-4" src="static/img/icon-info.png" />
              </el-tooltip>
            </template>
          </el-table-column>
          <el-table-column
            :min-width="120"
            prop="status"
            label="Status">
          </el-table-column>
          <el-table-column
            :min-width="200"
            prop="dateReceived"
            label="Date Received">
            <template v-slot="props">
              {{ lodash.get(props.row, 'dateReceived') | formatDate }}
            </template>
          </el-table-column>
          <el-table-column
            :min-width="260"
            prop="targetCompletionDate"
            label="Target Completion Date">
            <template v-slot="props">
              {{ lodash.get(props.row, 'targetCompletionDate') | formatDate }}
            </template>
          </el-table-column>
        </el-table>
      </div>
    </div>
    <div class="row">
      <div class="col-md-12">
        <div class="row" style="margin-top: 44px;">
          <div class="col-md-3"></div>
          <div class="col-md-6">
            <div>
              <v-select
                id="jobTemplate"
                name="jobTemplate"
                v-model="jobTemplate"
                @input="setJobTemplate"
                placeholder="Select a Job Template"
                label="label"
                value="value"
                style="min-width: 500px;"
                :disabled="isJobTemplateDisabled"
                :options="jobTemplatesForSelect">
              </v-select>
              <small class="text-danger" v-show="errors.has('jobTemplate')">
                {{ getError('jobTemplate') }}
              </small>
            </div>
          </div>
          <div class="col-md-3"></div>
        </div>
        <div class="row" style="margin-top: 12px;" v-if="canCreateBlankJob">
          <div class="col-md-3"></div>
          <div class="col-md-6">
            <div>
              <v-select
                name="processDefinition"
                placeholder="Select an Empty Job"
                v-model="processDefinition"
                @input="setProcessDefinition"
                label="label"
                value="value"
                style="min-width: 500px;"
                :disabled="isProcessDefinitionDisabled"
                :options="ptracProcessDefinitionsKvps">
              </v-select>
              <small class="text-danger" v-show="errors.has('processDefinition')">
                {{ getError('processDefinition') }}
              </small>
            </div>
          </div>
          <div class="col-md-3"></div>
        </div>
      </div>
    </div>
    <div class="row" style="min-height: 50px;"></div>
  </div>
</template>

<script>
import {mapGetters} from 'vuex'
import {Card} from 'src/components/UIComponents'
import {
  formatTaskFieldKey,
  notifyError,
  notifyWarning,
  objectBy,
  objectById,
  priorityLabel,
  sortBy,
  stripHTML
} from 'src/utilities/utils'
import Constants from 'src/utilities/constants'
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'
import {OK} from 'http-status-codes'
import indefinite from 'indefinite'
import _ from 'lodash'
import Permissions from 'src/utilities/permissions'

export default {
    props: ['data'],
    components: {
      vSelect,
      Card
    },
    computed: {
      ...mapGetters({
        user: 'security/user',
        userId: 'security/userId',
        taskTemplates: 'tasks/templates/results',
        allDepartments: 'projects/edit/departmentsForSelect',
        jobTemplatesForSelect: 'jobTemplates/kvpsForSelect',
        jobTemplatesByDepartment: 'jobTemplates/byDepartment',
        ptracProcessDefinitionsKvps: 'camunda/ptracKvpsForSelect',
        processDefinitions: 'camunda/ptracProcessDefinitions'
      }),
      projectId() {
        return this.$store.get('jobs/create@projectId')
      },
      departmentId() {
        return this.$store.get('jobs/create@departmentId')
      },
      departments() {
        if (this.data && this.data.projectDepartments && this.data.projectDepartments.length > 0) {
          return _.filter(this.allDepartments, (department) => this.data.projectDepartments.includes(department.value))
        }
        return this.$store.get('projects/edit/departments')
      },
      projectName() {
        const details = this.$store.get('projects/edit/details/all')
        return `${details.projectType} Project: ${details.name}`
      },
      projectNotes() {
        return this.$store.getters['projects/edit/details/notesByDepartment'][this.departmentId] || ''
      },
      canCreateBlankJob() {
        return this.$can('edit', Permissions.JOB_TEMPLATES)
      }
    },
    data() {
      return {
        lodash: _,
        selectedDepartmentId: null,
        jobTemplate: null,
        processDefinition: null,
        isProcessDefinitionDisabled: false,
        isJobTemplateDisabled: false,
        jobTableData: []
      }
    },
    methods: {
      getError(fieldName) {
        return this.errors.first(fieldName)
      },
      getDepartmentName() {
        const department = objectBy(this.allDepartments, 'value', this.departmentId)
        if (department) {
          return department.label
        }
        return ''
      },
      setProcessDefinition(selectedProcessDefinition) {
        if (!selectedProcessDefinition) {
          this.$store.commit('wizard/nextDisabled', true)
          this.isJobTemplateDisabled = false
          return
        }

        const processDefinition = this.$store.get('camunda/ptracProcessDefinitionById', selectedProcessDefinition.value)

        this.isJobTemplateDisabled = true

        // if a job template was selected first, undo all values that action may have set (unless remember flag has been set on the fields)
        this.$store.set('jobs/create@jobTemplateId', null)
        this.$store.set('jobs/create@processDefinitionId', processDefinition.id)
        this.$store.set('jobs/create@processDefinitionKey', processDefinition.key)

        const jobsRemember = this.$store.get('jobs@remember')

        if (!jobsRemember.client) {
          this.$store.set('jobs/create@clientId', null)
        }
        if (!jobsRemember.clientContact) {
          this.$store.set('jobs/create@clientContactId', null)
        }
        if (!jobsRemember.builder) {
          this.$store.set('jobs/create@builderId', null)
        }
        if (!jobsRemember.builderContact) {
          this.$store.set('jobs/create@builderContactId', null)
        }
        if (!jobsRemember.community) {
          this.$store.set('jobs/create@community', {})
        }
        this.$store.set('jobs/create@priority', Constants.PRIORITIES.MEDIUM.VALUE)

        this.handleSelection(processDefinition)
      },
      async setJobTemplate(selectedJobTemplate) {
        this.$eventHub.$emit('jobTemplateDeselected')

        if (!this.departmentId) {
          return
        }

        if (!selectedJobTemplate) {
          this.isProcessDefinitionDisabled = false
          this.$store.commit('wizard/nextDisabled', true)
          return
        }

        this.isProcessDefinitionDisabled = true

        const jobTemplate = objectById(this.jobTemplatesByDepartment, selectedJobTemplate.value)

        if (!jobTemplate) {
          console.error('Unable to load Job Template by ID', selectedJobTemplate.value)
          return
        }

        // if there are remembered fields, give them priority over fields in the templates
        const remember = this.$store.get('jobs/remember')

        if (jobTemplate.communityId && !remember.community) {
          const response = await this.$store.dispatch('fetchById', {
            endpoint: Constants.ENTITY_ENDPOINTS.COMMUNITIES,
            id: jobTemplate.communityId
          })

          if (OK === response.status) {
            this.$store.set('jobs/create@community', {
              id: response.data.id,
              name: response.data.name
            })
          }
        }

        this.$store.set('jobs/create@jobTemplateId', jobTemplate.id)
        this.$store.set('jobs/create@processDefinitionKey', jobTemplate.processDefinitionKey)

        if (!remember.client) {
          this.$store.set('jobs/create@clientId', jobTemplate.clientId)
          if (!remember.clientContact) {
            this.$store.set('jobs/create@clientContactId', jobTemplate.clientContactId)
          }
        }

        if (!remember.builder) {
          this.$store.set('jobs/create@builderId', jobTemplate.builderId)
          if (!remember.builderContact) {
            this.$store.set('jobs/create@builderContactId', jobTemplate.builderContactId)
          }
        }

        this.$store.set('jobs/create@priority', jobTemplate.priority)

        const processDefinition = objectBy(this.processDefinitions, 'key', jobTemplate.processDefinitionKey)

        if (!processDefinition) {
          notifyError(this, 'Unable to locate Process Definition by key: ' + jobTemplate.processDefinitionKey)
          return
        }

        this.$eventHub.$emit('jobTemplateSelected', jobTemplate)
        await this.handleSelection(processDefinition)
      },
      async handleSelection(processDefinition) {
        const jobNamesList = this.jobTableData.map(job => job.name);

        if (jobNamesList.includes(processDefinition.name)) {
          const message = `${indefinite(processDefinition.name, {capitalize: true})} job is already defined for this project.`
          notifyWarning(this, message)
        }

        this.$store.set('jobs/create@processDefinitionId', processDefinition.id)
        this.$store.set('jobs/create@processDefinitionKey', processDefinition.key)
        this.$store.set('jobs/create@name', processDefinition.name)
        this.$store.commit('wizard/nextDisabled', false)

        const projectId = this.$store.get('jobs/create@projectId')
        const departmentId = this.$store.get('jobs/create@departmentId')
        const response = await this.$store.dispatch('tasks/fetchReInspectionTask',
          { departmentId, projectId, jobName: encodeURIComponent(processDefinition.name)})

        if (OK === response.result) {
          const reInspectionTask = this.$store.getters['tasks/reInspectionTask']

          const payload = {
            jobName: processDefinition.name,
            task: reInspectionTask
          }
          if (reInspectionTask) {
            this.$eventHub.$emit('show-modal-2', 'Reinspection Task', payload, '800px')
          }
        }
      },
      validate() {
        return this.$validator.validateAll()
      },
      setJobCoordinator() {
        this.$store.set('jobs/create@coordinatorId', this.userId)
      },
      async maybeFetchDepartmentDependencies() {
        this.jobTableData = [] // start fresh - make sure no old data is hanging around

        if (this.departmentId) {
          await Promise.all([
            this.$store.dispatch('jobTemplates/fetchByDepartment', this.departmentId),
            this.$store.dispatch('camunda/fetchPtracProcessDefinitionsByDepartment', this.departmentId)
          ])
          const response = await this.$store.dispatch('jobs/fetchByProjectIdAndDepartmentId', {
            projectId: this.projectId,
            departmentId: this.departmentId
          })

          if (OK === response.status) {
            this.jobTableData = response.data

            // not an ideal pattern but other options weren't working
            for (const job of this.jobTableData) {
              const taskResponse = await this.$store.dispatch('tasks/fetchTasksByJobId', job.id)
              if (OK === taskResponse.status) {
                job.tasks = sortBy(taskResponse.data, 'createdAt')
              }
            }
          }
        }
      },
      getUserDisplayName(userId) {
        return this.$store.get('users/displayName', userId)
      },
      priorityValueToLabel(value) {
        return priorityLabel(value)
      },
      formatKey(task, fieldKey) {
        return formatTaskFieldKey(this.taskTemplates, task, fieldKey)
      },
      // called after the Quick Project + Job wizard creates a project
      setProjectAndDepartment(project) {
        this.$store.set('jobs/create@projectId', project.id)
        this.projectDepartments = project.departments
        this.setJobDepartment(project)
      },
      setJobDepartment(project) {
        let departments = []
        if (project && project.departments) {
          departments = project.departments
        } else {
          departments = this.$store.get('projects/edit@departments')
        }

        const defaultDepartmentId = _.get(this.user, 'preferences.global.defaultDepartment')

        let departmentId = null

        if (!departments || departments.length === 0) {
          departmentId = defaultDepartmentId
        } else if (departments.length === 1) {
          departmentId = departments[0]
        } else { // more than one department associated with project
          // if the user's default department is in the list of departments associated with the project, select it
          if (departments.includes(defaultDepartmentId)) {
            departmentId = defaultDepartmentId
          } else {
            // otherwise, use whatever department is first in the array
            departmentId = departments[0]
          }
        }

        if (departmentId) {
          this.selectedDepartmentId = departmentId
          this.setJobDepartmentId(departmentId)
        }
      },
      setJobDepartmentId(departmentId) {
        this.$store.set('jobs/create@partialResetCreate', null)
        this.$store.set('jobs/create@departmentId', departmentId)
        this.jobTemplate = null
        this.processDefinition = null
        this.isJobTemplateDisabled = false
      },
      handleEditJob(jobId) {
        this.$eventHub.$emit('show-modal-2', 'View Job', {id: jobId}, '1000px')
      },
      viewDescriptionNoHtml(description) {
        if (description) {
          return stripHTML(description)
        }
        return null
      },
      clearTemplateOrJobSelection() {
        this.jobTemplate = null
        this.isProcessDefinitionDisabled = false
        this.isJobTemplateDisabled = false
        this.$store.set('wizard/nextDisabled', true)
      }
    },
    beforeMount() {
      this.$store.cache.dispatch('tasks/templates/fetchAll')
    },
    mounted() {
      this.$eventHub.$once('projectCreated', this.setProjectAndDepartment)
      this.$eventHub.$on('navigateBackJobWizard', this.clearTemplateOrJobSelection)
      this.setJobDepartment()
      this.maybeFetchDepartmentDependencies()
      this.setJobCoordinator()
    },
    destroyed() {
      this.$eventHub.$off('navigateBackJobWizard')
    },
    watch: {
      departmentId: {
        handler: function (departmentId) {
          if (departmentId) {
            this.maybeFetchDepartmentDependencies()
          }
        }
      },
      jobTemplate: {
        handler: function (jobTemplate) {
          if (!jobTemplate) {
            this.$eventHub.$emit('jobTemplateDeselected')
          }
        }
      }
    }
  }
</script>

<style scoped>
  .card-wizard .tab-content {
    padding: 0 !important;
  }
</style>
